// Opening Hours Utilities - Check if a place is currently open

interface OpeningHours {
  displayText?: string;
  structured?: {
    [day: string]: { open: string; close: string } | null;
  };
}

// Day mapping (German to index, Sunday = 0)
const DAY_MAP: Record<string, number> = {
  'sonntag': 0,
  'montag': 1,
  'dienstag': 2,
  'mittwoch': 3,
  'donnerstag': 4,
  'freitag': 5,
  'samstag': 6,
};

// Common patterns in German opening hours text
const TIME_PATTERNS = {
  // "Mo-Fr 9-18 Uhr" or "Mo-Fr: 9:00-18:00"
  dayRange: /(\w+)-(\w+)[:\s]+(\d{1,2})[:\.]?(\d{2})?[-–](\d{1,2})[:\.]?(\d{2})?\s*(?:Uhr)?/gi,
  // "Mo 9-18" or "Montag: 9:00-18:00 Uhr"
  singleDay: /(\w+)[:\s]+(\d{1,2})[:\.]?(\d{2})?[-–](\d{1,2})[:\.]?(\d{2})?\s*(?:Uhr)?/gi,
  // "Taeglich 9-22 Uhr" or "Jeden Tag 10-20"
  daily: /(?:t[aä]glich|jeden\s*tag)[:\s]+(\d{1,2})[:\.]?(\d{2})?[-–](\d{1,2})[:\.]?(\d{2})?\s*(?:Uhr)?/gi,
  // "24 Stunden" or "rund um die Uhr"
  twentyFourSeven: /24\s*(?:stunden|h)|rund\s*um\s*die\s*uhr/gi,
};

function parseTimeToMinutes(hours: number, minutes: number = 0): number {
  return hours * 60 + minutes;
}

function getCurrentDayIndex(): number {
  return new Date().getDay();
}

function getCurrentTimeInMinutes(): number {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

function getDayIndex(dayName: string): number {
  const normalized = dayName.toLowerCase().trim();

  // Handle abbreviations
  if (normalized.startsWith('mo')) return 1;
  if (normalized.startsWith('di')) return 2;
  if (normalized.startsWith('mi')) return 3;
  if (normalized.startsWith('do')) return 4;
  if (normalized.startsWith('fr')) return 5;
  if (normalized.startsWith('sa')) return 6;
  if (normalized.startsWith('so')) return 0;

  return DAY_MAP[normalized] ?? -1;
}

function isDayInRange(currentDay: number, startDay: number, endDay: number): boolean {
  if (startDay <= endDay) {
    return currentDay >= startDay && currentDay <= endDay;
  }
  // Handle wrap-around (e.g., Fr-Mo)
  return currentDay >= startDay || currentDay <= endDay;
}

/**
 * Check if a place is currently open based on opening hours text
 */
export function isPlaceOpenNow(openingHours?: OpeningHours): boolean | null {
  if (!openingHours?.displayText) {
    return null; // Unknown
  }

  const text = openingHours.displayText.toLowerCase();
  const currentDay = getCurrentDayIndex();
  const currentTime = getCurrentTimeInMinutes();

  // Check for 24/7
  if (TIME_PATTERNS.twentyFourSeven.test(text)) {
    return true;
  }

  // Check for "closed" indicators
  if (text.includes('geschlossen') || text.includes('ruhetag')) {
    // Check if it's specifically today
    const todayNames = ['sonntag', 'montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag'];
    const todayName = todayNames[currentDay];
    if (text.includes(todayName) && text.includes('geschlossen')) {
      return false;
    }
  }

  // Reset regex lastIndex
  TIME_PATTERNS.daily.lastIndex = 0;
  TIME_PATTERNS.dayRange.lastIndex = 0;
  TIME_PATTERNS.singleDay.lastIndex = 0;

  // Check daily pattern
  let match = TIME_PATTERNS.daily.exec(text);
  if (match) {
    const openTime = parseTimeToMinutes(parseInt(match[1]), parseInt(match[2] || '0'));
    const closeTime = parseTimeToMinutes(parseInt(match[3]), parseInt(match[4] || '0'));
    return currentTime >= openTime && currentTime < closeTime;
  }

  // Check day range pattern
  TIME_PATTERNS.dayRange.lastIndex = 0;
  while ((match = TIME_PATTERNS.dayRange.exec(text)) !== null) {
    const startDay = getDayIndex(match[1]);
    const endDay = getDayIndex(match[2]);

    if (startDay !== -1 && endDay !== -1 && isDayInRange(currentDay, startDay, endDay)) {
      const openTime = parseTimeToMinutes(parseInt(match[3]), parseInt(match[4] || '0'));
      const closeTime = parseTimeToMinutes(parseInt(match[5]), parseInt(match[6] || '0'));
      return currentTime >= openTime && currentTime < closeTime;
    }
  }

  // Check single day pattern
  TIME_PATTERNS.singleDay.lastIndex = 0;
  while ((match = TIME_PATTERNS.singleDay.exec(text)) !== null) {
    const dayIndex = getDayIndex(match[1]);

    if (dayIndex === currentDay) {
      const openTime = parseTimeToMinutes(parseInt(match[2]), parseInt(match[3] || '0'));
      const closeTime = parseTimeToMinutes(parseInt(match[4]), parseInt(match[5] || '0'));
      return currentTime >= openTime && currentTime < closeTime;
    }
  }

  // If we couldn't parse, return null (unknown)
  return null;
}

/**
 * Get a human-readable status for opening hours
 */
export function getOpeningStatus(openingHours?: OpeningHours): {
  isOpen: boolean | null;
  statusText: string;
  statusColor: string;
} {
  const isOpen = isPlaceOpenNow(openingHours);

  if (isOpen === true) {
    return {
      isOpen: true,
      statusText: 'Jetzt geoeffnet',
      statusColor: 'text-green-600',
    };
  }

  if (isOpen === false) {
    return {
      isOpen: false,
      statusText: 'Geschlossen',
      statusColor: 'text-red-500',
    };
  }

  return {
    isOpen: null,
    statusText: '',
    statusColor: '',
  };
}
